/**
 * FundingMethodPicker — Bill 2026-05-12 lock per
 * `project_opening_deposit_funding_methods_20260512.md`.
 *
 * Replaces the flat opening-deposit input with a 5-method picker:
 *
 *   1. Internal Transfer (PREFERRED when related accts exist on cart;
 *      jXchange auto-exec post-AcctAdd)
 *   2. Cash / Check in hand (FSR receipt + deposit-slip workflow)
 *   3. Deferred (deadline-tracked; auto-escalates post-deadline)
 *   4. ACH (future stub — disabled with tooltip)
 *   5. Debit Card (future stub — disabled with tooltip)
 *
 * Persistence: method + amount + optional deadline are stored on
 * SelectedProduct.featureValues as a freeform map keyed by 'fundingMethod',
 * 'fundingDeadline'. Amount continues to flow through the canonical
 * openingDeposit field on SelectedProduct so downstream consumers (Review,
 * CartCommit, etc.) don't change. This avoids a shared-types migration for
 * tonight's ship; the admin-config matrix per (product × method) lives on
 * ProductFundingMethodAvailability when that endpoint wires up.
 *
 * Admin-config TODO: this component currently shows all 5 methods with
 * future-stubs disabled at the source. When ProductFundingMethodAvailability
 * is loaded, it should filter `methods` further per-product (Unavailable
 * hidden / Required defaulted / Recommended pre-selected / Available shown).
 */
import type { CSSProperties } from 'react';

export type FundingMethodKey =
  | 'internal-transfer'
  | 'cash-check'
  | 'deferred'
  | 'ach'
  | 'debit-card';

export interface FundingMethodPickerProps {
  amount: string;
  onAmountChange: (next: string) => void;
  method: FundingMethodKey | undefined;
  onMethodChange: (next: FundingMethodKey | undefined) => void;
  deadline: string;
  onDeadlineChange: (iso: string) => void;
  minDeposit: number | null;
  depositError: string | null;
  /** Bill 2026-10-14: the amount / deadline was just filled with a default — flash it so the
   *  officer sees it and reviews it. Changing the value is how they accept or correct it. */
  amountAutofilled?: boolean;
  deadlineAutofilled?: boolean;
}

interface MethodSpec {
  readonly key: FundingMethodKey;
  readonly title: string;
  readonly subtitle: string;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
}

const METHODS: ReadonlyArray<MethodSpec> = [
  {
    key: 'internal-transfer',
    title: 'Internal Transfer',
    subtitle: 'PREFERRED when an existing PCB account is on the cart. jXchange auto-executes post-AcctAdd.',
  },
  {
    key: 'cash-check',
    title: 'Cash or Check',
    subtitle: 'FSR receipts the funds in branch. Deposit-slip workflow on cart commit.',
  },
  {
    key: 'deferred',
    title: 'Deferred',
    subtitle: 'Customer funds within a deadline. Auto-escalates if missed.',
  },
  {
    key: 'ach',
    title: 'ACH (future)',
    subtitle: 'External-bank transfer via ACH. Phase 2 — not wired yet.',
    disabled: true,
    disabledReason: 'ACH transfer wiring is Phase 2. Use Internal Transfer, Cash/Check, or Deferred.',
  },
  {
    key: 'debit-card',
    title: 'Debit Card (future)',
    subtitle: 'Funded from an external debit card. Phase 2 — not wired yet.',
    disabled: true,
    disabledReason: 'Debit-card funding is Phase 2. Use Internal Transfer, Cash/Check, or Deferred.',
  },
];

const tileBase: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  textAlign: 'left',
  padding: 'var(--sp-4) var(--sp-5)',
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 'var(--r-md)',
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
  transition: 'border-color 120ms ease, background-color 120ms ease',
};

const tileSelected: CSSProperties = {
  borderColor: 'var(--pcb-green, #386732)',
  background: 'var(--ok-bg, #f0f8ee)',
  boxShadow: '0 0 0 2px var(--pcb-green, #386732) inset',
};

const tileDisabled: CSSProperties = {
  opacity: 0.55,
  cursor: 'not-allowed',
};

export function FundingMethodPicker({
  amount,
  onAmountChange,
  method,
  onMethodChange,
  deadline,
  onDeadlineChange,
  minDeposit,
  depositError,
  amountAutofilled = false,
  deadlineAutofilled = false,
}: FundingMethodPickerProps): JSX.Element {
  const isDeferred = method === 'deferred';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--sp-3)',
      }}>
        {METHODS.map((m) => {
          const isPicked = method === m.key;
          const style: CSSProperties = {
            ...tileBase,
            ...(isPicked ? tileSelected : null),
            ...(m.disabled ? tileDisabled : null),
          };
          return (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={isPicked}
              aria-disabled={m.disabled ? 'true' : 'false'}
              disabled={m.disabled}
              title={m.disabled ? m.disabledReason : undefined}
              onClick={() => onMethodChange(isPicked ? undefined : m.key)}
              style={style}
            >
              <div style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--pcb-blue-dark)',
                marginBottom: 'var(--sp-1)',
              }}>
                {m.title}
              </div>
              <div style={{
                fontSize: 11,
                color: 'var(--ink-600)',
                lineHeight: 1.4,
              }}>
                {m.subtitle}
              </div>
            </button>
          );
        })}
      </div>

      {/* Amount row — visible when a method is picked (always for now, since
          all funding methods carry a dollar amount). */}
      {method !== undefined && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label
            htmlFor="funding-amount"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}
          >
            Amount
            {minDeposit !== null && minDeposit > 0 && (
              <span style={{ fontWeight: 400, color: 'var(--ink-600)', marginLeft: 6 }}>
                (minimum ${minDeposit.toFixed(2).replace(/\.00$/, '')})
              </span>
            )}
          </label>
          <label className="rao-add2cart__deposit">
            <span aria-hidden="true" className="rao-add2cart__deposit-prefix">$</span>
            <input
              id="funding-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="0.00"
              value={amount}
              aria-label="Funding amount"
              aria-invalid={depositError !== null ? 'true' : 'false'}
              onChange={(e) => onAmountChange(e.currentTarget.value)}
              className={`rao-add2cart__deposit-input${amountAutofilled ? ' rao-autofill-flash' : ''}`}
            />
          </label>
          {amountAutofilled && (
            <p className="rao-autofill-note">Filled with the product&apos;s minimum opening deposit. Change it if the customer is depositing more.</p>
          )}
          {depositError !== null && (
            <p className="rao-add2cart__deposit-err" role="alert">{depositError}</p>
          )}
        </div>
      )}

      {/* Deferred-method-only deadline picker */}
      {isDeferred && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label
            htmlFor="funding-deadline"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}
          >
            Funding deadline
            <span style={{ fontWeight: 400, color: 'var(--ink-600)', marginLeft: 6 }}>
              (auto-escalates if missed)
            </span>
          </label>
          <input
            id="funding-deadline"
            type="date"
            value={deadline}
            onChange={(e) => onDeadlineChange(e.currentTarget.value)}
            className={deadlineAutofilled ? 'rao-autofill-flash' : undefined}
            style={{
              padding: '8px 10px',
              fontSize: 13,
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--ink-300)',
              maxWidth: 200,
            }}
          />
          {deadlineAutofilled && (
            <p className="rao-autofill-note">Filled with this product&apos;s default funding window. Change it if the customer agreed to a different date.</p>
          )}
        </div>
      )}
    </div>
  );
}
